import React, { useState } from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import EditIcon from '@mui/icons-material/Edit';
import FolderIcon from '@mui/icons-material/Folder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SellIcon from '@mui/icons-material/Sell';
import {
  Box,
  Button,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { green } from '@mui/material/colors';
import { FileData } from 'common/utils';
import { fileScanStatus } from 'config/config';

import NotScannedIcon from '../../assets/icons/NotScanned.svg';
import CustomModal from '../CustomModal';
import CustomIconButton from '../elements/IconButton';
import RenderFileIcon from './RenderFileIcon';

interface FileItemProps {
  id: string;
  type: 'file' | 'folder';
  name: string;
  modified: string;
  size?: string;
  onSelect?: (checked: boolean) => void;
  onClick?: () => void;
  downloadFile?: () => void;
  uploadedBy?: string;
  getFileVersions: (file: FileData) => void;
  file: FileData;
  onOptionsClick?: (file: FileData) => void;
  onCopyClick?: (file: FileData) => void;
  onMoveClick?: (file: FileData) => void;
  onRenameClick?: (file: FileData) => void;
}

const FileItem: React.FC<FileItemProps> = ({
  id,
  type,
  name,
  modified,
  size,
  onSelect,
  onClick,
  downloadFile,
  uploadedBy,
  getFileVersions,
  file,
  onOptionsClick,
  onCopyClick,
  onMoveClick,
  onRenameClick,
}) => {
  const theme = useTheme();
  const [showModal, setShowModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showCopySnackbar, setShowCopySnackbar] = useState(false);
  const [showMoveSnackbar, setShowMoveSnackbar] = useState(false);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDownloadFile = () => {
    if (file['scan-status'] !== fileScanStatus.scanned) {
      setShowModal(true);
    } else {
      downloadFile && downloadFile();
    }
  };
  return (
    <>
      <TableRow
        sx={{
          '&:hover': {
            backgroundColor: theme.palette.grey[100], // Light version of primary color
            cursor: type === 'folder' ? 'pointer' : 'default',
          },
        }}
      >
        {/* <TableCell padding="checkbox">
        <Checkbox
          color="primary"
          onChange={(e) => onSelect?.(e.target.checked)}
        />
      </TableCell> */}
        <TableCell align="center">
          <Typography variant="body2">
            {type !== 'folder' ? (
              <IconButton onClick={() => handleDownloadFile()}>
                <CloudDownloadIcon color="primary" />
              </IconButton>
            ) : (
              '-'
            )}
          </Typography>
        </TableCell>
        <TableCell>
          {type === 'folder' ? (
            <IconButton onClick={onClick}>
              <FolderIcon sx={{ color: '#ffcc00' }} />
            </IconButton>
          ) : (
            <RenderFileIcon fileType={name.split('.')[1]} />
          )}
        </TableCell>
        <TableCell onClick={type === 'folder' ? onClick : undefined}>
          <Typography variant="body1">{name}</Typography>
          {file.tags?.length && file.tags.length > 0 ? (
            <Box display="flex" alignItems="center" gap={1}>
              <SellIcon fontSize="small" color="primary" />
              {file.tags
                ?.filter((tag: { Key: string; Value: string }) => {
                  // Filter out tags with invalid values like "None"
                  return (
                    tag.Key &&
                    tag.Value &&
                    tag.Key !== 'None' &&
                    tag.Value !== 'None' &&
                    tag.Key.trim() !== '' &&
                    tag.Value.trim() !== ''
                  );
                })
                ?.map((tag: { Key: string; Value: string }) => (
                  <Chip key={tag.Value} size="small" label={tag.Value} />
                ))}
            </Box>
          ) : null}
        </TableCell>
        <TableCell align="center">
          {type === 'folder' ? (
            <Typography variant="body2">-</Typography>
          ) : (
            <>
              <CustomIconButton
                buttonType="primary"
                tooltipTitle="Options"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuOpen(e);
                }}
              >
                <MoreVertIcon />
              </CustomIconButton>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                onClick={handleMenuClose}
                onMouseLeave={handleMenuClose}
              >
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onOptionsClick?.(file);
                  }}
                >
                  <ListItemIcon>
                    <SellIcon fontSize="small" sx={{ color: '#1976d2' }} />
                  </ListItemIcon>
                  <ListItemText>Tags</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuClose();
                    setShowCopySnackbar(true);
                    onCopyClick?.(file);
                  }}
                >
                  <ListItemIcon>
                    <ContentCopyIcon
                      fontSize="small"
                      sx={{ color: '#1976d2' }}
                    />
                  </ListItemIcon>
                  <ListItemText>Copy</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuClose();
                    setShowMoveSnackbar(true);
                    onMoveClick?.(file);
                  }}
                >
                  <ListItemIcon>
                    <DriveFileMoveIcon
                      fontSize="small"
                      sx={{ color: '#1976d2' }}
                    />
                  </ListItemIcon>
                  <ListItemText>Move</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onRenameClick?.(file);
                  }}
                >
                  <ListItemIcon>
                    <EditIcon fontSize="small" sx={{ color: '#1976d2' }} />
                  </ListItemIcon>
                  <ListItemText>Rename</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </TableCell>
        <TableCell align="center">
          {file['scan-status'] === fileScanStatus.notScanned ? (
            <CustomIconButton tooltipTitle="Not scanned" buttonType="primary">
              <img src={NotScannedIcon} alt="Not scanned" height={30} />
            </CustomIconButton>
          ) : (
            <CustomIconButton
              tooltipTitle="No virus found"
              buttonType="success"
            >
              <CheckCircleIcon sx={{ color: green[400] }} />
            </CustomIconButton>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2">{modified}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{uploadedBy ?? '-'}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{size || '-'}</Typography>
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            component={Button}
            onClick={() => getFileVersions(file)}
            sx={{ textDecoration: 'none' }}
          >
            {type === 'folder' ? '-' : 'Get details'}
          </Typography>
        </TableCell>
      </TableRow>
      {/* Modal to show when file is not scanned */}
      <CustomModal
        open={showModal}
        onClose={() => setShowModal(false)}
        type="error"
        title="Cannot download !!"
        primaryButtonLabel="Ok"
        primaryButtonAction={() => setShowModal(false)}
      >
        <Typography variant="body1">
          File is not scanned, please try later...
        </Typography>
      </CustomModal>

      {/* Copy Snackbar */}
      <Snackbar
        open={showCopySnackbar}
        autoHideDuration={500}
        onClose={() => setShowCopySnackbar(false)}
        message="Copied!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#333',
            color: 'white',
            fontSize: '0.875rem',
            fontWeight: 500,
          },
        }}
      />
      <Snackbar
        open={showMoveSnackbar}
        autoHideDuration={500}
        onClose={() => setShowMoveSnackbar(false)}
        message="Moved!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#333',
            color: 'white',
            fontSize: '0.875rem',
            fontWeight: 500,
          },
        }}
      />
    </>
  );
};

export default FileItem;
